import React, { createContext, useContext, useEffect, useState } from "react";
import {
  indexedDbAddData,
  indexedDbAddStore,
  indexedDbDeleteAllData,
  indexedDbDeleteData,
  indexedDbDeleteStore,
  indexedDbGetAllData,
  indexedDbGetData,
  indexedDbGetObjectStore,
} from "../utils";
type IndexedDbContextType = {
  isReady: boolean;
  dbRequest?: IDBOpenDBRequest;
  addData: (objectStoreName: string, data: any, key: IDBValidKey) => Promise<any>;
  getData: (objectStoreName: string, key: IDBValidKey | IDBKeyRange) => Promise<any>;
  getAllData: (objectStoreName: string) => Promise<any>;
  deleteData: (objectStoreName: string, key: IDBValidKey | IDBKeyRange) => Promise<any>;
  deleteAllData: (objectStoreName: string) => Promise<any>;
  checkForMissingStores: (db: IDBDatabase, stores: string[]) => boolean;
};

type IndexedDbProviderProps = {
  dbName: string;
  version: number;
  stores: string[];
  onError?: (value: any) => void;
};

const IndexedDbContext = createContext<IndexedDbContextType | undefined>(undefined);

const dbContainsStore = (db: IDBDatabase, store: string): boolean => db.objectStoreNames.contains(store);

const checkForMissingStores = (db: IDBDatabase, stores: string[]): boolean => {
  return (
    stores.filter(function (store) {
      return dbContainsStore(db, store);
    }).length > 0
  );
};

const initDB = (
  setIsReady: (value: boolean) => void,
  setDb: (value: IDBDatabase) => void,
  dbName: string,
  stores: string[],
  version = 1,
  onError?: (error: any) => void,
): IDBOpenDBRequest => {
  const dbRequest = window.indexedDB.open(dbName, version);

  dbRequest.onupgradeneeded = (changeEvent: IDBVersionChangeEvent) => {
    //@ts-ignore;
    const db: IDBDatabase = changeEvent.target.result;
    const existingStores: string[] = Array.from(db.objectStoreNames);
    const toRemove: string[] = existingStores.filter((store) => stores.includes(store));
    const promises: Promise<any>[] = [];

    toRemove.forEach((store) => {
      promises.push(indexedDbDeleteStore(db, store));
    });
    stores.forEach((store) => {
      promises.push(indexedDbAddStore(db, store));
    });

    Promise.allSettled(promises).then((promises) => {
      setIsReady(true);
    });
  };

  dbRequest.onerror = (error) => {
    if (onError) {
      onError(error);
    }
  };

  dbRequest.onsuccess = (success: Event) => {
    //@ts-ignore;
    const db: IDBDatabase = success.target.result;
    setDb(db);
    setIsReady(true);
  };

  return dbRequest;
};

const IndexedDbProvider: React.FC<React.PropsWithChildren<IndexedDbProviderProps>> = ({
  dbName,
  version,
  stores,
  onError,
  children,
}) => {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [db, setDb] = useState<IDBDatabase>();

  const getObjectStore = (objectStoreName: string): IDBObjectStore | undefined => {
    return indexedDbGetObjectStore(db, objectStoreName);
  };

  const addData = (objectStoreName: string, data: any, key?: IDBValidKey): Promise<IDBValidKey> => {
    return indexedDbAddData(db, objectStoreName, data, key);
  };

  const getData = (objectStoreName: string, key: IDBValidKey | IDBKeyRange): Promise<any> => {
    return indexedDbGetData(db, objectStoreName, key);
  };

  const getAllData = async (objectStoreName: string): Promise<any> => {
    return indexedDbGetAllData(db, objectStoreName);
  };

  const deleteData = async (objectStoreName: string, key: IDBValidKey | IDBKeyRange): Promise<any> => {
    return indexedDbDeleteData(db, objectStoreName, key);
  };

  const deleteAllData = async (objectStoreName: string): Promise<any> => {
    return indexedDbDeleteAllData(db, objectStoreName);
  };

  useEffect(() => {
    initDB(setIsReady, setDb, dbName, stores, version, onError);
  }, [dbName, stores, onError]);

  return (
    <IndexedDbContext.Provider
      value={{
        isReady,
        addData,
        getData,
        getAllData,
        deleteData,
        deleteAllData,
        checkForMissingStores,
      }}
    >
      {children}
    </IndexedDbContext.Provider>
  );
};

const useIndexedDB = (): IndexedDbContextType => {
  const indexedDbContext: IndexedDbContextType | undefined = useContext<IndexedDbContextType | undefined>(
    IndexedDbContext,
  );
  if (indexedDbContext === undefined) {
    throw new Error(`useIndexedDB must be used within a IndexedDbProvider`);
  }
  return indexedDbContext;
};

export { useIndexedDB, IndexedDbContext, IndexedDbProvider };
